<template>
  <div class="geo-root" :class="{ fallback: usingFallback }">
    <div ref="mapEl" class="geo-map" />
    <div class="geo-tint" />
    <div class="geo-radar" />
    <div class="geo-scan" />
  </div>
</template>

<script>
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'

const QIANSHAN = [30.6315, 116.5814]

export default {
  name: 'GeoMap',
  props: {
    services: { type: Array, default: () => [] },
    counts: { type: Object, default: () => ({}) },
    activeId: { type: String, default: '' }
  },
  data() {
    return {
      map: null,
      markers: {},
      circles: [],
      usingFallback: false,
      tileErrors: 0
    }
  },
  watch: {
    services: {
      deep: true,
      handler() {
        this.syncMarkers()
      }
    },
    counts: {
      deep: true,
      handler() {
        this.syncMarkers()
      }
    },
    activeId(id) {
      if (id) this.focusActive(id)
      else this.fitAll()
    }
  },
  mounted() {
    this.initMap()
  },
  beforeDestroy() {
    if (this._popTimer) clearTimeout(this._popTimer)
    if (this.map) {
      this.map.remove()
      this.map = null
    }
  },
  methods: {
    initMap() {
      const map = L.map(this.$refs.mapEl, {
        center: QIANSHAN,
        zoom: 13,
        minZoom: 8,
        maxZoom: 18,
        zoomControl: false,
        attributionControl: false
      })
      this.addBaseLayers(map)
      L.control.zoom({ position: 'bottomright' }).addTo(map)
      this.map = map
      this.syncMarkers()
      this.$nextTick(() => {
        map.invalidateSize()
        if (this.activeId) this.focusActive(this.activeId)
        else this.fitAll()
      })
    },
    tdtTileUrl(type, tk) {
      // 开发走本地代理，去掉 Referer；白名单放开后生产可直连天地图
      if (process.env.NODE_ENV === 'development') {
        return `/tdt-tiles/DataServer?T=${type}&x={x}&y={y}&l={z}&tk=${tk}`
      }
      return `https://t{s}.tianditu.gov.cn/DataServer?T=${type}&x={x}&y={y}&l={z}&tk=${tk}`
    },
    addBaseLayers(map) {
      const tk = process.env.VUE_APP_TIANDITU_TK || ''
      if (!tk) {
        this.useFallback(map)
        return
      }
      const opts = {
        maxZoom: 18,
        minZoom: 1,
        subdomains: ['0', '1', '2', '3', '4', '5', '6', '7']
      }
      // 天地图卫星影像 + 中文注记，略压暗以配合大屏
      map.createPane('tdtImg')
      map.getPane('tdtImg').style.zIndex = 200
      map.getPane('tdtImg').style.filter = 'brightness(0.72) saturate(1.2) contrast(1.08)'
      map.createPane('tdtCia')
      map.getPane('tdtCia').style.zIndex = 250
      map.getPane('tdtCia').style.mixBlendMode = 'screen'
      const img = L.tileLayer(this.tdtTileUrl('img_w', tk), Object.assign({}, opts, { pane: 'tdtImg' }))
      const cia = L.tileLayer(this.tdtTileUrl('cia_w', tk), Object.assign({}, opts, { pane: 'tdtCia' }))
      img.on('tileerror', () => {
        this.tileErrors += 1
        if (this.tileErrors >= 4) this.useFallback(map, [img, cia])
      })
      img.addTo(map)
      cia.addTo(map)
      this.tdtLayers = [img, cia]
    },
    useFallback(map, removeLayers) {
      if (this.usingFallback) return
      this.usingFallback = true
      ;(removeLayers || this.tdtLayers || []).forEach((layer) => {
        if (map.hasLayer(layer)) map.removeLayer(layer)
      })
      L.tileLayer('https://map.geoq.cn/ArcGIS/rest/services/ChinaOnlineStreetPurplishBlue/MapServer/tile/{z}/{y}/{x}', {
        maxZoom: 16,
        attribution: 'GeoQ'
      }).addTo(map)
    },
    focusActive(id) {
      const sid = id || this.activeId
      const marker = sid && this.markers[sid]
      if (!this.map) return
      if (!marker) {
        this.fitAll()
        return
      }
      const ll = marker.getLatLng()
      this.map.flyTo(ll, Math.max(this.map.getZoom(), 13), { duration: 0.55 })
    },
    fitAll() {
      if (!this.map) return
      const pts = this.services
        .filter(s => s.id !== 'nursing' && s.short !== '院内照护' && s.lat != null && s.lng != null)
        .map(s => [s.lat, s.lng])
      if (!pts.length) {
        this.map.setView(QIANSHAN, 12)
        return
      }
      this.map.fitBounds(pts, {
        paddingTopLeft: [330, 170],
        paddingBottomRight: [330, 210],
        maxZoom: 13,
        animate: true
      })
    },
    countOf(id) {
      return (this.counts[id] && this.counts[id].total) || 0
    },
    servedOf(id) {
      return (this.counts[id] && this.counts[id].served) || 0
    },
    usersOf(id) {
      return (this.counts[id] && this.counts[id].users) || 0
    },
    syncMarkers() {
      if (!this.map) return
      Object.keys(this.markers).forEach(id => {
        this.map.removeLayer(this.markers[id])
      })
      this.markers = {}
      ;(this.circles || []).forEach(c => {
        if (this.map.hasLayer(c)) this.map.removeLayer(c)
      })
      this.circles = []
      this.services.forEach(s => {
        if (s.id === 'nursing' || s.short === '院内照护' || s.lat == null || s.lng == null) return
        const html = `<div class="geo-pin" style="--c:${s.color}">
          <i class="geo-ring"></i>
          <i class="geo-core"></i>
          <b>${s.area || s.short}</b>
          <em>${s.short}</em>
        </div>`
        const icon = L.divIcon({
          className: 'geo-icon',
          html,
          iconSize: [108, 68],
          iconAnchor: [54, 18]
        })
        const marker = L.marker([s.lat, s.lng], { icon })
        const total = this.countOf(s.id)
        const served = this.servedOf(s.id)
        const users = this.usersOf(s.id)
        const items = (s.tags || []).slice(0, 2).join(' / ') || '标准服务'
        const pop = `<div class="geo-pop">
          <div class="geo-pop-title">${s.name}</div>
          <div class="geo-pop-sub">潜山市${s.area || ''} · ${s.short}</div>
          <div class="geo-pop-kv"><span>有效订单</span><b>${total} 单</b></div>
          <div class="geo-pop-kv"><span>服务记录</span><b>${served} 次</b></div>
          <div class="geo-pop-kv"><span>服务人数</span><b>${users} 人</b></div>
          <div class="geo-pop-kv"><span>服务项目</span><b>${items}</b></div>
          <div class="geo-pop-kv"><span>覆盖区域</span><b>${s.area || '潜山市'}</b></div>
          <button type="button" class="geo-pop-btn" data-sid="${s.id}">
            <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path fill="currentColor" d="M12 5c-5 0-9.27 3.11-11 7.5C2.73 16.89 7 20 12 20s9.27-3.11 11-7.5C21.27 8.11 17 5 12 5zm0 12.5c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8a3 3 0 100 6 3 3 0 000-6z"/></svg>
            在线查看
          </button>
        </div>`
        marker.bindPopup(pop, {
          className: 'geo-leaflet-pop',
          closeButton: false,
          autoClose: false,
          closeOnClick: false,
          maxWidth: 280,
          offset: [0, -6]
        })
        const clearClose = () => {
          if (this._popTimer) {
            clearTimeout(this._popTimer)
            this._popTimer = null
          }
        }
        const scheduleClose = () => {
          clearClose()
          this._popTimer = setTimeout(() => {
            marker.closePopup()
          }, 180)
        }
        marker.on('mouseover', () => {
          clearClose()
          marker.openPopup()
        })
        marker.on('mouseout', scheduleClose)
        marker.on('popupopen', e => {
          const wrap = e.popup.getElement()
          if (!wrap) return
          L.DomEvent.on(wrap, 'mouseenter', clearClose)
          L.DomEvent.on(wrap, 'mouseleave', scheduleClose)
          const btn = wrap.querySelector('.geo-pop-btn')
          if (btn) {
            L.DomEvent.on(btn, 'click', ev => {
              L.DomEvent.stop(ev)
              this.$emit('enter', s.id)
            })
          }
        })
        const circle = L.circle([s.lat, s.lng], {
          radius: 2200,
          color: s.color,
          weight: 1.5,
          fillColor: s.color,
          fillOpacity: 0.12,
          opacity: 0.85
        })
        circle.addTo(this.map)
        this.circles.push(circle)
        marker.addTo(this.map)
        this.markers[s.id] = marker
      })
      if (!this.activeId) this.fitAll()
    },
    resize() {
      if (this.map) this.map.invalidateSize()
    }
  }
}
</script>

<style lang="scss">
.geo-root {
  position: absolute;
  inset: 0;
  z-index: 0;
  background: #04101c;
}
.geo-map {
  position: relative;
  z-index: 2;
  width: 100%;
  height: 100%;
}
.geo-map.leaflet-container {
  background: #041018;
}
.geo-tint, .geo-radar, .geo-scan {
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
}
.geo-tint {
  background:
    radial-gradient(1200px 600px at 50% 30%, rgba(0, 180, 200, 0.03), transparent 70%),
    linear-gradient(180deg, rgba(2, 12, 24, 0.02), rgba(2, 10, 20, 0.06));
}
.geo-radar {
  background: conic-gradient(from 0deg, transparent 0 78%, rgba(46, 230, 214, 0.08) 88%, transparent 100%);
  animation: geo-spin 12s linear infinite;
  mix-blend-mode: screen;
}
.geo-scan {
  background: linear-gradient(180deg, transparent 0%, rgba(46, 230, 214, 0.06) 48%, transparent 52%);
  background-size: 100% 220%;
  animation: geo-scan 6s linear infinite;
}
.geo-icon { background: none !important; border: none !important; }
.geo-pin {
  position: relative;
  text-align: center;
  color: #e8fbff;
}
.geo-core {
  display: block;
  width: 14px;
  height: 14px;
  margin: 0 auto;
  border-radius: 50%;
  background: var(--c);
  box-shadow: 0 0 10px var(--c), 0 0 22px var(--c);
}
.geo-ring {
  position: absolute;
  left: 50%;
  top: 0;
  width: 26px;
  height: 26px;
  margin-left: -13px;
  margin-top: -6px;
  border: 1px solid var(--c);
  border-radius: 50%;
  animation: geo-pulse 1.8s ease-out infinite;
}
.geo-pin b {
  display: block;
  margin-top: 6px;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 1px;
  text-shadow: 0 0 8px #041018, 0 0 6px var(--c);
  white-space: nowrap;
}
.geo-pin em {
  display: block;
  font-style: normal;
  font-size: 11px;
  color: #c8f6f0;
  text-shadow: 0 0 8px #041018;
  white-space: nowrap;
}
.geo-leaflet-pop .leaflet-popup-content-wrapper {
  background: linear-gradient(180deg, rgba(10, 48, 86, 0.96), rgba(8, 32, 64, 0.96));
  border: 1px solid rgba(72, 196, 230, 0.7);
  border-radius: 8px;
  color: #d6f4f8;
  box-shadow: 0 10px 28px rgba(0, 10, 24, 0.45), 0 0 18px rgba(40, 180, 220, 0.22);
}
.geo-leaflet-pop .leaflet-popup-tip-container { display: none; }
.geo-leaflet-pop .leaflet-popup-content {
  margin: 14px 16px 12px;
  width: 232px !important;
}
.geo-pop-title {
  color: #e8fbff;
  font-weight: 700;
  font-size: 14px;
  margin-bottom: 4px;
  letter-spacing: 0.5px;
}
.geo-pop-sub {
  margin-bottom: 10px;
  font-size: 12px;
  color: #7ec8d4;
}
.geo-pop-kv {
  display: grid;
  grid-template-columns: 72px 1fr;
  gap: 10px;
  line-height: 1.9;
  font-size: 13px;
  span { text-align: right; color: #8eb8cc; }
  b { font-weight: 500; color: #d6eef8; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
}
.geo-pop-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin: 12px auto 2px;
  width: 132px;
  height: 32px;
  cursor: pointer;
  border-radius: 16px;
  border: 1px solid #3ec8e8;
  background: rgba(20, 90, 130, 0.35);
  color: #7ee8ff;
  letter-spacing: 1px;
  font-size: 13px;
  svg { display: block; }
  &:hover { background: rgba(40, 140, 180, 0.45); color: #fff; }
}
.leaflet-bar {
  border: 1px solid rgba(46, 230, 214, 0.4) !important;
  background: rgba(6, 24, 40, 0.9) !important;
}
.leaflet-bar a {
  background: transparent !important;
  color: #2ee6d6 !important;
  border-bottom-color: rgba(46, 230, 214, 0.25) !important;
  width: 30px !important;
  height: 30px !important;
  line-height: 30px !important;
}
@keyframes geo-pulse {
  0% { transform: scale(1); opacity: 0.7; }
  100% { transform: scale(2.2); opacity: 0; }
}
@keyframes geo-spin {
  to { transform: rotate(360deg); }
}
@keyframes geo-scan {
  0% { background-position: 0 -100%; }
  100% { background-position: 0 200%; }
}
</style>
